// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { typeSensorVariable, TypeSensorVariableDO } from '../../../configuration/requests';
import { waste, sensor, SensorDO } from '../../requests';

import GraphChart from './graph/GraphChart';

interface Props {
  rowId?: number;
}

interface State {
  update: string;
  loading: boolean;

  graphInput: any;
  typeVariableArray: TypeSensorVariableDO[];
  variables: any[];

  typeSensorId?: number;
  dateIni?: string;
  dateEnd?: string;
}

export default class Graph extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 1);
    const dEnd: Date = new Date();

    this.pid = window.getStr();
    this.mounted = false;

    this.state = {
      update: window.getStr(),
      loading: false,

      graphInput: {},
      typeVariableArray: [],

      variables: [],
      dateIni: moment(dIni).format('YYYY-MM-DD'),
      dateEnd: moment(dEnd).format('YYYY-MM-DD'),
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props;

    this.loadingStart();
    try {
      const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(rowId));
      const { typeSensorId } = sensorInfo || {};
      const typeVariableArray: TypeSensorVariableDO[] = await typeSensorVariable<TypeSensorVariableDO[]>('list', undefined, { typeSensorId }) || [];

      this.setLocalState({
        typeVariableArray,
        typeSensorId,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public submit = async (): Promise<void> => {
    const { rowId } = this.props;
    const { dateIni, dateEnd, variables } = this.state;

    const req: Record<string, any> = {
      mode: 'CONTAINER',
      containerId: rowId,
      from: dateIni,
      to: dateEnd
    };

    this.loadingStart();
    try {
      let data: any[] = [];
      // Unloads info
      const [data1, data2] = await Promise.all([
        await waste<any[]>('unload_graph', undefined, req) || [],
        await waste<any[]>('unload_graph', undefined, { ...req, verified: true }) || []
      ]);

      // ALL
      // const data1: any = await waste('unload_graph', undefined, req);
      const gdata1: any[] = this.readingsLoad(data1, 'column', 0, true, 'ALL-UNLOADS');
      data = data.concat(gdata1);
      // VERIFIED
      // req.verified = true;
      // const data2: any = await waste('unload_graph', undefined, req);
      const gdata2: any[] = this.readingsLoad(data2, 'column', 0, true, 'VER-UNLOADS');
      data = data.concat(gdata2);

      // Downlaods reads
      if (!_.isEmpty(variables)) {
        const data3: any[] = await waste('unload_reads', undefined, { ...req, wheels: variables }) || [];
        if (!_.isEmpty(data3)) {
          for (let i: number = 0; i < (variables.length + 1); i++) {
            const gdata3: any[] = this.readingsLoad([data3[i]], 'line', 0, false);
            data = data.concat(gdata3);
          }
        }
      }

      this.setLocalState({
        update: window.getStr(),
        graphInput: {
          data,
        },
      });
    } catch (error) {
      console.error(error);
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public toTimestamp = (strDate: string): number => {
    const datum: number = Date.parse(strDate);
    return datum / 1000;
  };

  public readingsLoad = (reqdata: any[], type: string, yAxis: number, visible: boolean, name?: string): any[] => {
    const parsedData: any[] = reqdata.map((e: any) => {
      const serieData: any[] = e.data.map((point: any) => {
        const dateTimeStamp: string = point[0];
        const intervalTimeArray: [number, any] = [this.toTimestamp(dateTimeStamp) * 1000, point[1]];
        return intervalTimeArray;
      });

      return {
        type,
        yAxis,
        zIndex: 2,
        data: serieData,
        name: (name || e.name),
        visible
      };
    });

    return parsedData;
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false });
    window.store.dispatch(finishProcess(this.pid));
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setLocalState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setLocalState({
      variables: selection,
    });
  };

  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public getData = (): DataSelect2[] => {
    const { typeVariableArray } = this.state;

    return typeVariableArray.filter((value: TypeSensorVariableDO) => {
      return value.variableName.startsWith('Wheel');
    }).map((value: TypeSensorVariableDO): DataSelect2 => {
      return { id: (value.order - 1), text: `${value.variableName}` };
    });
  };

  public render(): JSX.Element {
    const { rowId } = this.props;
    const { variables, dateIni, dateEnd, update, graphInput } = this.state;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                fullscreenbutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
              >
                <div className="container" style={{ paddingTop: '0px' }}>
                  <UiValidate>
                    <div className="row smart-form">
                      <div className="col-xs-6 col-sm-6 col-md-6 col-lg-6">
                        <div className="inline-controls">
                          <label className="label">
                            Date Ini
                          </label>
                          <input
                            type="date"
                            name="dateIni"
                            className="input-mandatory"
                            value={dateIni}
                            onChange={this.handleChange}
                            required
                          />
                          &nbsp;&nbsp;
                        </div>
                        <div className="inline-controls">
                          <label className="label">
                            Date End
                          </label>
                          <input
                            type="date"
                            name="dateEnd"
                            className="input-mandatory"
                            value={dateEnd}
                            onChange={this.handleChange}
                            required
                          />
                          &nbsp;&nbsp;
                        </div>
                        <div className="inline-controls">
                          <label className="label">
                            Wheels
                          </label>
                          <Select2
                            multiple
                            id="variables"
                            name="variables"
                            value={variables}
                            onSelect={this.handleChangeMultiple}
                            onUnselect={this.handleChangeMultiple}
                            data={this.getData()}
                            options={{ placeholder: 'Select variables' }}
                            style={{ width: '400px' }}
                          />
                        </div>
                        <div className="inline-controls">
                          <button
                            type="button"
                            onClick={(): void => { this.submit().catch(() => { /* pass */ }); }}
                            className="btn btn-default"
                            style={{ padding: '4px 12px', margin: '1px' }}
                          >
                            GO
                          </button>
                        </div>
                      </div>
                    </div>
                  </UiValidate>
                  <br /><br />
                  <GraphChart update={update} graphInput={graphInput} />
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
